// The upload of files dropped or pasted into a terminal pane (see term/drops.ts): one line in a pill in the middle of
// the pane, not a toast. A spinner, "Uploading • NAME" (or "N files") and Cancel (or Esc in the terminal) while it
// runs; when it fails (one that stops moving fails by itself, term/upload.ts) it says why and Dismiss (it goes by
// itself after a few seconds). It overlays the pane, so the terminal never moves.
// A quick upload shows nothing (the pasted path is the feedback). The banner never takes the keyboard from the terminal.

import { IconX } from '@tabler/icons-react';
import { useRef } from 'react';
import { machineName, useStore, type Upload } from '../store';
import { dismissUpload, formatSize } from '../term/drops';
import { peekTerm } from '../term/manager';
import { cancelUpload } from '../term/upload';
import { Mark, Tool, presence, usePresence } from './ds';

export function UploadBanner({ sid }: { sid: number }) {
  const up = useStore((s) => s.uploads.find((u) => u.session === sid));
  // while it leaves (--dur-exit-md) it keeps the words it had
  const last = useRef<Upload | undefined>(undefined);
  if (up) last.current = up;
  const p = usePresence(!!up, 150);
  const u = up ?? last.current;
  if (!p.mounted || !u) return null;
  const failed = u.error !== undefined;
  const pct = u.total ? Math.min(100, Math.floor((u.sent * 100) / u.total)) : 0;
  // a screen reader hears it at every quarter, not every byte
  const quarter = Math.min(75, Math.floor(pct / 25) * 25);
  return (
    <div
      className="up-banner ds-float"
      data-size="md"
      data-state={failed ? 'error' : 'uploading'}
      data-testid="upload-banner"
      title={failed ? `Upload failed: ${u.error}` : `Uploading ${u.name} to ${machineName()} · ${pct}% of ${formatSize(u.total)}`}
      {...presence(p)}
      // Clicking its button must leave the keyboard with the terminal.
      onPointerDown={(e) => {
        if ((e.target as HTMLElement).closest('button')) e.preventDefault();
      }}
    >
      {failed ? <Mark tone="fault" /> : <span className="loading-ring" aria-hidden="true" />}
      {failed ? (
        <span className="up-title" key="error" role="alert" data-testid="upload-error">
          <b>Upload failed</b>
          <span className="up-dot"> • </span>
          {u.error}
        </span>
      ) : (
        <span className="up-title" key="uploading" role="status">
          <span className="sr-only">{quarter ? `Uploading ${u.name}, ${quarter}%` : `Uploading ${u.name}`}</span>
          <span aria-hidden="true">
            <b>Uploading</b>
            <span className="up-dot"> • </span>
            <span data-testid="upload-name">{u.name}</span>
          </span>
        </span>
      )}
      {failed ? (
        <Tool
          icon={IconX}
          size="xs"
          label="Dismiss"
          align="end"
          data-testid="upload-dismiss"
          onClick={() => {
            dismissUpload(u.id);
            peekTerm(sid)?.focus();
          }}
        />
      ) : (
        <Tool
          icon={IconX}
          size="xs"
          label="Cancel upload"
          sub="Esc"
          align="end"
          data-testid="upload-cancel"
          onClick={() => {
            cancelUpload(sid);
            peekTerm(sid)?.focus();
          }}
        />
      )}
    </div>
  );
}
